import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { ElNotification } from 'element-plus'

import { tGlobal } from '@/i18n'

import type {
  AlertStats,
  AlphaOverview,
  AnalysisSettings,
  AnalysisResult,
  AuthorizationState,
  BootstrapResponse,
  Capabilities,
  Channel,
  ChannelConfig,
  ChannelSyncState,
  GuestAccount,
  GuestAccountCreate,
  GuestAccountUpdate,
  HistorySyncJob,
  HistorySyncRequest,
  IntelligenceGraph,
  MarketAlert,
  MarketAlertEpisode,
  MarketMover,
  MarketStatus,
  MemoryRuntimeStatus,
  MonitorStatus,
  Notice,
  NotificationSettings,
  OnchainCryptoGraph,
  OnchainEpisodeEvent,
  OnchainEpisodeEventsPage,
  OnchainHolderHistoryResponse,
  OnchainHolderSnapshot,
  OnchainRuntimeStatus,
  OnchainRadarPage,
  OnchainRadarState,
  OnchainRadarView,
  OnchainRiskStatus,
  OnchainSmartFlowResponse,
  OnchainWalletCluster,
  Profile,
  SenderStat,
  ServerEvent,
  TelegramDialogOption,
  TelegramMessage,
  Urgency,
} from '@/types/monitor'
import { resolveMediaUrl, resolveSenderAvatarUrl } from '@/utils/format'

const INITIAL_MESSAGE_LIMIT = 150
const MESSAGE_PAGE_SIZE = 100
const MESSAGE_MEMORY_LIMIT = 10_000

const EMPTY_STATUS: MonitorStatus = {
  connected: false,
  authorization: 'not_required',
  source: 'idle',
  channels_active: 0,
  channels_failed: [],
  total_messages: 0,
  media_downloaded: 0,
  total_analyzed: 0,
  uptime_seconds: 0,
  rate_limited: false,
  rate_limit_seconds: 0,
  rate_limit_request: '',
  analysis_running: false,
  analysis_pending_messages: 0,
  analysis_last_run_at: null,
  analysis_next_run_at: null,
  history_sync_running: false,
  history_sync_channel: null,
  history_synced_last_run: 0,
  history_sync_error: null,
  latest_urgency: 'NORMAL',
  last_error: null,
}

const EMPTY_CAPABILITIES: Capabilities = {
  telegram_enabled: false,
  demo_enabled: false,
  ai_provider: 'unknown',
  media_downloads: false,
  market_alerts: false,
  market_provider: 'unknown',
  history_sync: false,
  memory_enabled: false,
  memory_semantic_requested: false,
  onchain_enabled: false,
  onchain_radar_enabled: false,
  onchain_chain_id: 'unknown',
}

const EMPTY_MEMORY_STATUS: MemoryRuntimeStatus = {
  enabled: false,
  semantic_requested: false,
  embeddings_ready: false,
  pgvector_available: false,
  search_backend: 'disabled',
  model_id: null,
  model_fingerprint: null,
  embedding_dimensions: 1024,
  asset_source: null,
  last_error: null,
  counts: { total: 0, pending: 0, ready: 0, failed: 0 },
  backfill: { processed: 0, indexed: 0, failed: 0, last_error: null, updated_at: null },
  search: {
    requests: 0,
    degraded_requests: 0,
    timeouts: 0,
    semantic_timeouts: 0,
    last_duration_ms: 0,
    last_degraded_reason: null,
    updated_at: null,
  },
}

const EMPTY_MARKET_STATUS: MarketStatus = {
  enabled: false,
  running: false,
  provider: 'disabled',
  equities_active: 0,
  crypto_active: 0,
  quotes_last_cycle: 0,
  failed_last_cycle: 0,
  alerts_open: 0,
  episodes_open: 0,
  asset_refresh_running: false,
  asset_refresh_error: null,
  last_asset_refresh_at: null,
  last_poll_at: null,
  next_poll_at: null,
  last_latency_ms: null,
  degraded: false,
  last_error: null,
}

const EMPTY_ALERT_STATS: AlertStats = {
  total: 0,
  unacknowledged: 0,
  critical: 0,
  high: 0,
  medium: 0,
  low: 0,
  equities: 0,
  crypto: 0,
}

interface ApiErrorPayload {
  error?: {
    code?: string
    message?: string
  }
}

export const useMonitorStore = defineStore('monitor', () => {
  const profile = ref<Profile | null>(null)
  const status = ref<MonitorStatus>({ ...EMPTY_STATUS })
  const messages = ref<TelegramMessage[]>([])
  const messagesLoadingOlder = ref(false)
  const messagesHasMore = ref(true)
  const messageSenderFilter = ref<number | null>(null)
  const senderStats = ref<SenderStat[]>([])
  const analyses = ref<AnalysisResult[]>([])
  const alphaOverview = ref<AlphaOverview | null>(null)
  const alphaLoading = ref(false)
  const intelligenceGraph = ref<IntelligenceGraph | null>(null)
  const intelligenceLoading = ref(false)
  const channels = ref<Channel[]>([])
  const telegramDialogs = ref<TelegramDialogOption[]>([])
  const telegramDialogsLoading = ref(false)
  const analysisSettings = ref<AnalysisSettings | null>(null)
  const notificationSettings = ref<NotificationSettings | null>(null)
  const guestAccounts = ref<GuestAccount[]>([])
  const capabilities = ref<Capabilities>({ ...EMPTY_CAPABILITIES })
  const memoryStatus = ref<MemoryRuntimeStatus>({
    ...EMPTY_MEMORY_STATUS,
    counts: { ...EMPTY_MEMORY_STATUS.counts },
    backfill: { ...EMPTY_MEMORY_STATUS.backfill },
  })
  const marketStatus = ref<MarketStatus>({ ...EMPTY_MARKET_STATUS })
  const marketEpisodes = ref<MarketAlertEpisode[]>([])
  const marketAlerts = ref<MarketAlert[]>([])
  const marketMovers = ref<MarketMover[]>([])
  const alertStats = ref<AlertStats>({ ...EMPTY_ALERT_STATS })
  const onchainRadarItems = ref<OnchainRadarView[]>([])
  const onchainRadarLoading = ref(false)
  const onchainRadarError = ref<string | null>(null)
  const onchainStatus = ref<OnchainRuntimeStatus | null>(null)
  const onchainStatusLoading = ref(false)
  const onchainStatusError = ref<string | null>(null)
  const onchainHolderHistory = ref<OnchainHolderSnapshot[]>([])
  const onchainHolderHistoryLoading = ref(false)
  const onchainHolderHistoryError = ref<string | null>(null)
  const onchainHolderHistoryWindowSeconds = ref(21_600)
  const onchainSmartFlow = ref<OnchainSmartFlowResponse | null>(null)
  const onchainSmartFlowLoading = ref(false)
  const onchainSmartFlowError = ref<string | null>(null)
  const onchainSmartFlowWindowSeconds = ref(21_600)
  const onchainCryptoGraph = ref<OnchainCryptoGraph | null>(null)
  const onchainCryptoGraphLoading = ref(false)
  const onchainCryptoGraphError = ref<string | null>(null)
  const onchainCryptoGraphWindowSeconds = ref(86_400)
  const onchainRadarStateFilter = ref<OnchainRadarState | null>(null)
  const onchainRadarRiskFilter = ref<OnchainRiskStatus | null>(null)
  const onchainRadarSourceFilter = ref('')
  const onchainRadarTokenQuery = ref('')
  const onchainRadarNextBefore = ref<string | null>(null)
  const onchainRadarNextBeforeId = ref<string | null>(null)
  const onchainRadarLoadedAt = ref<string | null>(null)
  const selectedOnchainRadar = ref<OnchainRadarView | null>(null)
  const onchainWalletClusters = ref<OnchainWalletCluster[]>([])
  const onchainWalletClustersLoading = ref(false)
  const onchainWalletClustersError = ref<string | null>(null)
  const onchainEpisodeEvents = ref<OnchainEpisodeEvent[]>([])
  const onchainEpisodeTimelineLoading = ref(false)
  const onchainEpisodeNextBefore = ref<string | null>(null)
  const onchainEpisodeNextBeforeId = ref<string | null>(null)
  const channelSyncStates = ref<ChannelSyncState[]>([])
  const historySyncJobs = ref<HistorySyncJob[]>([])
  const notices = ref<Notice[]>([])
  const loading = ref(true)
  const bootstrapError = ref<string | null>(null)
  const websocketState = ref<'connecting' | 'connected' | 'reconnecting' | 'closed'>('closed')
  const protocolVersion = ref<number | null>(null)

  let socket: WebSocket | null = null
  let reconnectTimer: number | null = null
  let reconnectAttempts = 0
  let disposed = false
  let noticeSequence = 0
  let senderStatsTimer: number | null = null
  let onchainRadarRefreshTimer: number | null = null
  let onchainHolderHistoryRequestSequence = 0
  let onchainSmartFlowRequestSequence = 0
  let onchainCryptoGraphRequestSequence = 0

  const latestAnalysis = computed(() => analyses.value[0] ?? null)
  const mediaMessages = computed(() => messages.value.filter((message) => message.has_media))
  const urgency = computed<Urgency>(
    () => latestAnalysis.value?.urgency ?? status.value.latest_urgency ?? 'NORMAL',
  )
  const isRealtime = computed(
    () => websocketState.value === 'connected' && status.value.connected,
  )
  const unacknowledgedAlerts = computed(
    () => marketAlerts.value.filter((alert) => !alert.acknowledged).length,
  )
  const historySyncBusy = computed(
    () =>
      status.value.history_sync_running ||
      historySyncJobs.value.some((job) => job.status === 'queued' || job.status === 'running'),
  )

  async function bootstrap(): Promise<void> {
    loading.value = true
    bootstrapError.value = null
    try {
      const payload = await apiRequest<BootstrapResponse>(
        '/api/v1/bootstrap?message_limit=150&analysis_limit=30&alert_limit=200',
      )
      profile.value = payload.profile
      analysisSettings.value = {
        enabled: payload.profile.analysis_enabled,
        interval_seconds: payload.profile.summary_interval_seconds,
        batch_trigger_enabled: payload.profile.analysis_batch_trigger_enabled,
        batch_size: payload.profile.summary_batch_size,
        max_messages: payload.profile.analysis_max_messages,
        system_prompt: payload.profile.analysis_system_prompt,
        prompt_template: payload.profile.analysis_prompt_template,
      }
      notificationSettings.value = payload.profile.notification
      status.value = payload.status
      messages.value = payload.messages.map(normalizeMessage).sort(newestFirst)
      messagesHasMore.value = payload.messages.length >= INITIAL_MESSAGE_LIMIT
      analyses.value = payload.analyses.sort(analysisNewestFirst)
      channels.value = payload.channels
      capabilities.value = payload.capabilities
      memoryStatus.value = payload.memory_status
      marketStatus.value = payload.market_status
      marketEpisodes.value = payload.market_episodes.sort(episodeNewestFirst)
      marketAlerts.value = payload.market_alerts.sort(alertNewestFirst)
      marketMovers.value = payload.market_movers
      alertStats.value = payload.alert_stats
      channelSyncStates.value = payload.channel_sync_states
      historySyncJobs.value = payload.history_sync_jobs
      document.title = payload.profile.app_title
      void loadIntelligenceGraph().catch(() => undefined)
    } catch (error) {
      bootstrapError.value = errorMessage(error)
      throw error
    } finally {
      loading.value = false
    }
  }

  async function refreshMemoryStatus(): Promise<void> {
    memoryStatus.value = await apiRequest<MemoryRuntimeStatus>('/api/v1/memory/status')
  }

  function connectRealtime(): void {
    if (disposed || socket?.readyState === WebSocket.OPEN || socket?.readyState === WebSocket.CONNECTING) {
      return
    }
    websocketState.value = reconnectAttempts > 0 ? 'reconnecting' : 'connecting'
    socket = new WebSocket(websocketUrl())
    if (senderStatsTimer === null) {
      senderStatsTimer = window.setInterval(() => {
        void loadSenderStats().catch(() => undefined)
      }, 30_000)
    }
    socket.addEventListener('open', () => {
      websocketState.value = 'connected'
      reconnectAttempts = 0
      void refreshMonitoringScope().catch((error) => {
        pushNotice('warning', errorMessage(error))
      })
    })
    socket.addEventListener('message', (event: MessageEvent<string>) => {
      try {
        applyEvent(JSON.parse(event.data) as ServerEvent)
      } catch {
        pushNotice('warning', tGlobal('notices.unreadableRealtime'))
      }
    })
    socket.addEventListener('close', () => {
      socket = null
      if (!disposed) scheduleReconnect()
    })
    socket.addEventListener('error', () => socket?.close())
  }

  function scheduleReconnect(): void {
    if (reconnectTimer !== null || disposed) return
    websocketState.value = 'reconnecting'
    const delay = Math.min(15_000, 750 * 2 ** Math.min(reconnectAttempts, 5))
    reconnectAttempts += 1
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = null
      connectRealtime()
    }, delay)
  }

  function applyEvent(event: ServerEvent): void {
    switch (event.type) {
      case 'ready':
        protocolVersion.value = event.data.protocol_version
        break
      case 'message':
        upsertMessage(normalizeMessage(event.data))
        break
      case 'analysis':
        analyses.value = [event.data, ...analyses.value.filter((item) => item.id !== event.data.id)].slice(
          0,
          30,
        )
        void loadAlphaOverview().catch(() => undefined)
        void loadIntelligenceGraph().catch(() => undefined)
        break
      case 'status':
        status.value = event.data
        break
      case 'caption_updated': {
        const message = messages.value.find((item) => item.id === event.data.message_id)
        if (message) message.translated_caption = event.data.translated_caption
        break
      }
      case 'sender_avatar_updated':
        for (const message of messages.value) {
          if (message.sender_telegram_id === event.data.sender_id) {
            message.sender_avatar_url = event.data.avatar_url
          }
        }
        for (const stat of senderStats.value) {
          if (stat.telegram_id === event.data.sender_id) {
            stat.avatar_url = event.data.avatar_url
          }
        }
        break
      case 'history_sync_job':
        applyHistorySyncJob(event.data)
        break
      case 'market_quote': {
        const mover = marketMovers.value.find((item) => item.asset.key === event.data.asset_key)
        if (mover) mover.quote = event.data
        break
      }
      case 'market_alert':
        applyMarketAlert(event.data)
        break
      case 'market_episode':
        applyMarketEpisode(event.data)
        break
      case 'onchain_episode':
        scheduleOnchainRadarRefresh()
        break
      case 'market_movers':
        marketMovers.value = event.data.movers
        break
      case 'market_status':
        marketStatus.value = event.data
        break
      case 'notice':
        pushNotice(normalizeNoticeLevel(event.data.level), event.data.message)
        break
    }
  }

  function upsertMessage(message: TelegramMessage): void {
    if (
      messageSenderFilter.value !== null &&
      message.sender_telegram_id !== messageSenderFilter.value
    ) {
      return
    }
    messages.value = [message, ...messages.value.filter((item) => item.id !== message.id)]
      .sort(newestFirst)
      .slice(0, MESSAGE_MEMORY_LIMIT)
  }

  async function loadOlderMessages(): Promise<number> {
    if (messagesLoadingOlder.value || !messagesHasMore.value || messages.value.length === 0) return 0
    const oldest = messages.value[messages.value.length - 1]
    if (!oldest) return 0

    messagesLoadingOlder.value = true
    try {
      const params = new URLSearchParams({
        limit: String(MESSAGE_PAGE_SIZE),
        before: oldest.published_at,
        before_id: oldest.id,
      })
      if (messageSenderFilter.value !== null) {
        params.set('sender_id', String(messageSenderFilter.value))
      }
      const older = await apiRequest<TelegramMessage[]>(`/api/v1/messages?${params.toString()}`)
      const merged = new Map(messages.value.map((message) => [message.id, message]))
      for (const message of older) {
        const normalized = normalizeMessage(message)
        merged.set(normalized.id, normalized)
      }
      messages.value = Array.from(merged.values()).sort(newestFirst).slice(0, MESSAGE_MEMORY_LIMIT)
      messagesHasMore.value = older.length >= MESSAGE_PAGE_SIZE && messages.value.length < MESSAGE_MEMORY_LIMIT
      return older.length
    } finally {
      messagesLoadingOlder.value = false
    }
  }

  async function loadSenderStats(): Promise<SenderStat[]> {
    const stats = await apiRequest<SenderStat[]>('/api/v1/messages/senders?limit=200')
    senderStats.value = stats
    return stats
  }

  async function setMessageSenderFilter(senderId: number | null): Promise<void> {
    if (messageSenderFilter.value === senderId) return
    messageSenderFilter.value = senderId
    const params = new URLSearchParams({ limit: String(INITIAL_MESSAGE_LIMIT) })
    if (senderId !== null) params.set('sender_id', String(senderId))
    const refreshed = await apiRequest<TelegramMessage[]>(`/api/v1/messages?${params.toString()}`)
    messages.value = refreshed.map(normalizeMessage).sort(newestFirst)
    messagesHasMore.value = refreshed.length >= INITIAL_MESSAGE_LIMIT
  }

  async function triggerAnalysis(): Promise<number> {
    const result = await apiRequest<{ accepted: boolean; message_count: number }>(
      '/api/v1/analyses/trigger',
      { method: 'POST' },
    )
    pushNotice('info', tGlobal('notices.briefingQueued', { count: result.message_count }))
    return result.message_count
  }

  async function loadAlphaOverview(): Promise<AlphaOverview> {
    if (alphaLoading.value && alphaOverview.value) return alphaOverview.value
    alphaLoading.value = true
    try {
      const overview = await apiRequest<AlphaOverview>('/api/v1/alpha/overview?signal_limit=5000')
      alphaOverview.value = overview
      return overview
    } finally {
      alphaLoading.value = false
    }
  }

  async function loadIntelligenceGraph(): Promise<IntelligenceGraph> {
    if (intelligenceLoading.value && intelligenceGraph.value) return intelligenceGraph.value
    intelligenceLoading.value = true
    try {
      const graph = await apiRequest<IntelligenceGraph>('/api/v1/intelligence/graph?limit=500')
      intelligenceGraph.value = graph
      return graph
    } finally {
      intelligenceLoading.value = false
    }
  }

  async function loadOnchainRadar(reset = true): Promise<OnchainRadarView[]> {
    if (onchainRadarLoading.value) return onchainRadarItems.value
    if (!reset && (!onchainRadarNextBefore.value || !onchainRadarNextBeforeId.value)) {
      return onchainRadarItems.value
    }

    onchainRadarLoading.value = true
    onchainRadarError.value = null
    try {
      const params = new URLSearchParams({ limit: '100' })
      if (onchainRadarStateFilter.value) params.set('state', onchainRadarStateFilter.value)
      if (onchainRadarRiskFilter.value) params.set('risk_status', onchainRadarRiskFilter.value)
      if (onchainRadarSourceFilter.value.trim()) params.set('source', onchainRadarSourceFilter.value.trim())
      if (onchainRadarTokenQuery.value.trim()) params.set('q', onchainRadarTokenQuery.value.trim())
      if (!reset && onchainRadarNextBefore.value && onchainRadarNextBeforeId.value) {
        params.set('before', onchainRadarNextBefore.value)
        params.set('before_id', onchainRadarNextBeforeId.value)
      }
      const page = await apiRequest<OnchainRadarPage>(`/api/v1/onchain/radar?${params.toString()}`)
      if (reset) {
        onchainRadarItems.value = page.items
      } else {
        const merged = new Map(onchainRadarItems.value.map((item) => [item.asset_id, item]))
        for (const item of page.items) merged.set(item.asset_id, item)
        onchainRadarItems.value = Array.from(merged.values()).sort(onchainRadarNewestFirst)
      }
      onchainRadarNextBefore.value = page.next_before
      onchainRadarNextBeforeId.value = page.next_before_id
      onchainRadarLoadedAt.value = new Date().toISOString()
      return onchainRadarItems.value
    } catch (error) {
      onchainRadarError.value = errorMessage(error)
      throw error
    } finally {
      onchainRadarLoading.value = false
    }
  }

  async function loadOnchainStatus(): Promise<OnchainRuntimeStatus> {
    if (onchainStatusLoading.value && onchainStatus.value) return onchainStatus.value
    onchainStatusLoading.value = true
    onchainStatusError.value = null
    try {
      const status = await apiRequest<OnchainRuntimeStatus>('/api/v1/onchain/status')
      onchainStatus.value = status
      return status
    } catch (error) {
      onchainStatusError.value = errorMessage(error)
      throw error
    } finally {
      onchainStatusLoading.value = false
    }
  }

  async function loadOnchainHolderHistory(
    assetId: string,
    windowSeconds = onchainHolderHistoryWindowSeconds.value,
  ): Promise<OnchainHolderSnapshot[]> {
    const requestSequence = ++onchainHolderHistoryRequestSequence
    onchainHolderHistoryLoading.value = true
    onchainHolderHistoryError.value = null
    onchainHolderHistoryWindowSeconds.value = windowSeconds
    try {
      const params = new URLSearchParams({
        window_seconds: String(windowSeconds),
        limit: '512',
      })
      const response = await apiRequest<OnchainHolderHistoryResponse>(
        `/api/v1/onchain/radar/${encodeURIComponent(assetId)}/holder-history?${params.toString()}`,
      )
      if (requestSequence === onchainHolderHistoryRequestSequence) {
        onchainHolderHistoryWindowSeconds.value = response.window_seconds
        onchainHolderHistory.value = response.items
      }
      return response.items
    } catch (error) {
      if (requestSequence === onchainHolderHistoryRequestSequence) {
        onchainHolderHistory.value = []
        onchainHolderHistoryError.value = errorMessage(error)
      }
      return []
    } finally {
      if (requestSequence === onchainHolderHistoryRequestSequence) {
        onchainHolderHistoryLoading.value = false
      }
    }
  }

  async function setOnchainHolderHistoryWindow(windowSeconds: number): Promise<OnchainHolderSnapshot[]> {
    onchainHolderHistoryWindowSeconds.value = windowSeconds
    const assetId = selectedOnchainRadar.value?.asset_id
    if (!assetId) return onchainHolderHistory.value
    return loadOnchainHolderHistory(assetId, windowSeconds)
  }

  async function loadOnchainSmartFlow(
    assetId: string,
    windowSeconds = onchainSmartFlowWindowSeconds.value,
  ): Promise<OnchainSmartFlowResponse | null> {
    const requestSequence = ++onchainSmartFlowRequestSequence
    onchainSmartFlowLoading.value = true
    onchainSmartFlowError.value = null
    onchainSmartFlowWindowSeconds.value = windowSeconds
    try {
      const params = new URLSearchParams({ window_seconds: String(windowSeconds) })
      const response = await apiRequest<OnchainSmartFlowResponse>(
        `/api/v1/onchain/radar/${encodeURIComponent(assetId)}/smart-flow?${params.toString()}`,
      )
      if (requestSequence === onchainSmartFlowRequestSequence) {
        onchainSmartFlowWindowSeconds.value = response.window_seconds
        onchainSmartFlow.value = response
      }
      return response
    } catch (error) {
      if (requestSequence === onchainSmartFlowRequestSequence) {
        onchainSmartFlow.value = null
        onchainSmartFlowError.value = errorMessage(error)
      }
      return null
    } finally {
      if (requestSequence === onchainSmartFlowRequestSequence) {
        onchainSmartFlowLoading.value = false
      }
    }
  }

  async function setOnchainSmartFlowWindow(windowSeconds: number): Promise<OnchainSmartFlowResponse | null> {
    onchainSmartFlowWindowSeconds.value = windowSeconds
    const assetId = selectedOnchainRadar.value?.asset_id
    if (!assetId) return onchainSmartFlow.value
    return loadOnchainSmartFlow(assetId, windowSeconds)
  }

  async function loadOnchainCryptoGraph(
    assetId: string,
    windowSeconds = onchainCryptoGraphWindowSeconds.value,
  ): Promise<OnchainCryptoGraph | null> {
    const requestSequence = ++onchainCryptoGraphRequestSequence
    onchainCryptoGraphLoading.value = true
    onchainCryptoGraphError.value = null
    onchainCryptoGraphWindowSeconds.value = windowSeconds
    try {
      const params = new URLSearchParams({ window_seconds: String(windowSeconds) })
      const response = await apiRequest<OnchainCryptoGraph>(
        `/api/v1/onchain/radar/${encodeURIComponent(assetId)}/graph?${params.toString()}`,
      )
      if (requestSequence === onchainCryptoGraphRequestSequence) {
        onchainCryptoGraph.value = response
      }
      return response
    } catch (error) {
      if (requestSequence === onchainCryptoGraphRequestSequence) {
        onchainCryptoGraph.value = null
        onchainCryptoGraphError.value = errorMessage(error)
      }
      return null
    } finally {
      if (requestSequence === onchainCryptoGraphRequestSequence) {
        onchainCryptoGraphLoading.value = false
      }
    }
  }

  async function setOnchainCryptoGraphWindow(windowSeconds: number): Promise<OnchainCryptoGraph | null> {
    onchainCryptoGraphWindowSeconds.value = windowSeconds
    const assetId = selectedOnchainRadar.value?.asset_id
    if (!assetId) return onchainCryptoGraph.value
    return loadOnchainCryptoGraph(assetId, windowSeconds)
  }

  async function setOnchainRadarFilters(
    state: OnchainRadarState | null,
    risk: OnchainRiskStatus | null,
    source = '',
    tokenQuery = '',
  ): Promise<OnchainRadarView[]> {
    onchainRadarStateFilter.value = state
    onchainRadarRiskFilter.value = risk
    onchainRadarSourceFilter.value = source.trim()
    onchainRadarTokenQuery.value = tokenQuery.trim()
    onchainRadarNextBefore.value = null
    onchainRadarNextBeforeId.value = null
    return loadOnchainRadar(true)
  }

  async function loadMoreOnchainRadar(): Promise<OnchainRadarView[]> {
    return loadOnchainRadar(false)
  }

  async function selectOnchainRadar(assetId: string): Promise<OnchainRadarView> {
    const detail = await apiRequest<OnchainRadarView>(`/api/v1/onchain/radar/${encodeURIComponent(assetId)}`)
    selectedOnchainRadar.value = detail
    onchainHolderHistory.value = []
    onchainHolderHistoryError.value = null
    onchainSmartFlow.value = null
    onchainSmartFlowError.value = null
    onchainCryptoGraph.value = null
    onchainCryptoGraphError.value = null
    onchainWalletClusters.value = []
    onchainWalletClustersError.value = null
    onchainEpisodeEvents.value = []
    onchainEpisodeNextBefore.value = null
    onchainEpisodeNextBeforeId.value = null
    await Promise.all([
      loadOnchainEpisodeEvents(detail.episode.id, true),
      loadOnchainWalletClusters(detail.asset_id),
      loadOnchainHolderHistory(detail.asset_id),
      loadOnchainSmartFlow(detail.asset_id),
      loadOnchainCryptoGraph(detail.asset_id),
    ])
    return detail
  }

  function clearOnchainRadarSelection(): void {
    selectedOnchainRadar.value = null
    onchainHolderHistoryRequestSequence += 1
    onchainHolderHistory.value = []
    onchainHolderHistoryLoading.value = false
    onchainHolderHistoryError.value = null
    onchainSmartFlowRequestSequence += 1
    onchainSmartFlow.value = null
    onchainSmartFlowLoading.value = false
    onchainSmartFlowError.value = null
    onchainCryptoGraphRequestSequence += 1
    onchainCryptoGraph.value = null
    onchainCryptoGraphLoading.value = false
    onchainCryptoGraphError.value = null
    onchainWalletClusters.value = []
    onchainWalletClustersError.value = null
    onchainEpisodeEvents.value = []
    onchainEpisodeNextBefore.value = null
    onchainEpisodeNextBeforeId.value = null
  }

  async function loadOnchainWalletClusters(assetId: string): Promise<OnchainWalletCluster[]> {
    if (onchainWalletClustersLoading.value) return onchainWalletClusters.value
    onchainWalletClustersLoading.value = true
    onchainWalletClustersError.value = null
    try {
      const clusters = await apiRequest<OnchainWalletCluster[]>(
        `/api/v1/onchain/radar/${encodeURIComponent(assetId)}/clusters?limit=100`,
      )
      onchainWalletClusters.value = clusters
      return clusters
    } catch (error) {
      onchainWalletClusters.value = []
      onchainWalletClustersError.value = errorMessage(error)
      return []
    } finally {
      onchainWalletClustersLoading.value = false
    }
  }

  async function loadOnchainEpisodeEvents(
    episodeId: string,
    reset = true,
  ): Promise<OnchainEpisodeEvent[]> {
    if (onchainEpisodeTimelineLoading.value) return onchainEpisodeEvents.value
    if (!reset && (!onchainEpisodeNextBefore.value || !onchainEpisodeNextBeforeId.value)) {
      return onchainEpisodeEvents.value
    }

    onchainEpisodeTimelineLoading.value = true
    try {
      const params = new URLSearchParams({ limit: '100' })
      if (!reset && onchainEpisodeNextBefore.value && onchainEpisodeNextBeforeId.value) {
        params.set('before', onchainEpisodeNextBefore.value)
        params.set('before_id', onchainEpisodeNextBeforeId.value)
      }
      const page = await apiRequest<OnchainEpisodeEventsPage>(
        `/api/v1/onchain/episodes/${encodeURIComponent(episodeId)}/events?${params.toString()}`,
      )
      if (reset) {
        onchainEpisodeEvents.value = page.items
      } else {
        const merged = new Map(onchainEpisodeEvents.value.map((item) => [item.id, item]))
        for (const item of page.items) merged.set(item.id, item)
        onchainEpisodeEvents.value = Array.from(merged.values()).sort(onchainEpisodeEventNewestFirst)
      }
      onchainEpisodeNextBefore.value = page.next_before
      onchainEpisodeNextBeforeId.value = page.next_before_id
      return onchainEpisodeEvents.value
    } finally {
      onchainEpisodeTimelineLoading.value = false
    }
  }

  async function loadMoreOnchainEpisodeEvents(): Promise<OnchainEpisodeEvent[]> {
    const episodeId = selectedOnchainRadar.value?.episode.id
    if (!episodeId) return onchainEpisodeEvents.value
    return loadOnchainEpisodeEvents(episodeId, false)
  }

  function scheduleOnchainRadarRefresh(): void {
    if (onchainRadarLoadedAt.value === null || onchainRadarRefreshTimer !== null) return
    onchainRadarRefreshTimer = window.setTimeout(() => {
      onchainRadarRefreshTimer = null
      void refreshOnchainRadarAfterEpisode().catch(() => undefined)
    }, 600)
  }

  async function refreshOnchainRadarAfterEpisode(): Promise<void> {
    const selectedAssetId = selectedOnchainRadar.value?.asset_id ?? null
    await loadOnchainRadar(true)
    if (selectedAssetId) await selectOnchainRadar(selectedAssetId)
  }

  async function requestLoginCode(phone: string): Promise<AuthorizationState> {
    const response = await apiRequest<{ authorization: AuthorizationState }>(
      '/api/v1/telegram/auth/request-code',
      jsonRequest({ phone }),
    )
    status.value.authorization = response.authorization
    return response.authorization
  }

  async function verifyLoginCode(
    code: string,
  ): Promise<{ authorization: AuthorizationState; hint: string | null }> {
    const response = await apiRequest<{ authorization: AuthorizationState; hint: string | null }>(
      '/api/v1/telegram/auth/verify-code',
      jsonRequest({ code }),
    )
    status.value.authorization = response.authorization
    return response
  }

  async function verifyPassword(password: string): Promise<AuthorizationState> {
    const response = await apiRequest<{ authorization: AuthorizationState }>(
      '/api/v1/telegram/auth/verify-password',
      jsonRequest({ password }),
    )
    status.value.authorization = response.authorization
    return response.authorization
  }

  async function loadTelegramDialogs(): Promise<TelegramDialogOption[]> {
    if (!capabilities.value.telegram_enabled || status.value.authorization !== 'authorized') {
      telegramDialogs.value = []
      return []
    }
    telegramDialogsLoading.value = true
    try {
      const dialogs = await apiRequest<TelegramDialogOption[]>('/api/v1/telegram/dialogs')
      telegramDialogs.value = dialogs
      return dialogs
    } finally {
      telegramDialogsLoading.value = false
    }
  }

  async function joinAllChannels(): Promise<{ joined: number; failed: string[] }> {
    const response = await apiRequest<{ joined: number; failed: string[] }>(
      '/api/v1/telegram/channels/join-all',
      { method: 'POST' },
    )
    pushNotice(
      response.failed.length > 0 ? 'warning' : 'info',
      tGlobal('channels.joinResult', { joined: response.joined, failed: response.failed.length }),
    )
    return response
  }

  async function saveChannelSettings(nextChannels: ChannelConfig[]): Promise<number> {
    const response = await apiRequest<{ channels: ChannelConfig[]; resolved_channels: number }>(
      '/api/v1/settings/channels',
      {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ channels: nextChannels }),
      },
    )
    if (profile.value) profile.value.channels = response.channels
    await refreshMonitoringScope()
    if (telegramDialogs.value.length > 0) {
      await loadTelegramDialogs()
    }
    pushNotice('info', tGlobal('channels.saved', { count: response.resolved_channels }))
    return response.resolved_channels
  }

  async function saveAnalysisSettings(settings: AnalysisSettings): Promise<AnalysisSettings> {
    const response = await apiRequest<AnalysisSettings>('/api/v1/settings/analysis', {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(settings),
    })
    analysisSettings.value = response
    if (profile.value) {
      profile.value.analysis_enabled = response.enabled
      profile.value.summary_interval_seconds = response.interval_seconds
      profile.value.analysis_batch_trigger_enabled = response.batch_trigger_enabled
      profile.value.summary_batch_size = response.batch_size
      profile.value.analysis_max_messages = response.max_messages
      profile.value.analysis_system_prompt = response.system_prompt
      profile.value.analysis_prompt_template = response.prompt_template
    }
    pushNotice('info', tGlobal('analysisSettings.saved'))
    return response
  }

  async function saveNotificationSettings(
    settings: NotificationSettings,
  ): Promise<NotificationSettings> {
    const response = await apiRequest<NotificationSettings>('/api/v1/settings/notifications', {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(settings),
    })
    notificationSettings.value = response
    if (profile.value) profile.value.notification = response
    pushNotice('info', tGlobal('notificationSettings.saved'))
    return response
  }

  async function loadGuestAccounts(): Promise<GuestAccount[]> {
    const response = await apiRequest<GuestAccount[]>('/api/v1/settings/guests')
    guestAccounts.value = response
    return response
  }

  async function createGuestAccount(account: GuestAccountCreate): Promise<GuestAccount> {
    const response = await apiRequest<GuestAccount>('/api/v1/settings/guests', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(account),
    })
    await loadGuestAccounts()
    pushNotice('info', tGlobal('guestAccounts.created'))
    return response
  }

  async function updateGuestAccount(id: string, account: GuestAccountUpdate): Promise<GuestAccount> {
    const response = await apiRequest<GuestAccount>(`/api/v1/settings/guests/${id}`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(account),
    })
    guestAccounts.value = guestAccounts.value.map((item) => (item.id === id ? response : item))
    pushNotice('info', tGlobal('guestAccounts.updated'))
    return response
  }

  async function deleteGuestAccount(id: string): Promise<void> {
    await apiRequest<void>(`/api/v1/settings/guests/${id}`, { method: 'DELETE' })
    guestAccounts.value = guestAccounts.value.filter((item) => item.id !== id)
    pushNotice('info', tGlobal('guestAccounts.deleted'))
  }

  async function revokeGuestAccountSessions(id: string): Promise<GuestAccount> {
    const response = await apiRequest<GuestAccount>(`/api/v1/settings/guests/${id}/revoke-sessions`, {
      method: 'POST',
    })
    guestAccounts.value = guestAccounts.value.map((item) => (item.id === id ? response : item))
    pushNotice('info', tGlobal('guestAccounts.sessionsRevoked'))
    return response
  }

  async function revokeAllGuestSessions(): Promise<void> {
    await apiRequest<{ revoked_sessions: number }>('/api/v1/settings/guests/revoke-sessions', {
      method: 'POST',
    })
    guestAccounts.value = guestAccounts.value.map((item) => ({ ...item, active_sessions: 0 }))
    pushNotice('info', tGlobal('guestAccounts.allSessionsRevoked'))
  }

  async function acknowledgeAlert(id: string): Promise<MarketAlert> {
    const acknowledged = await apiRequest<MarketAlert>(`/api/v1/alerts/${id}/acknowledge`, {
      method: 'POST',
    })
    marketAlerts.value = marketAlerts.value.map((alert) =>
      alert.id === acknowledged.id ? acknowledged : alert,
    )
    alertStats.value = await apiRequest<AlertStats>('/api/v1/alerts/stats')
    marketStatus.value.alerts_open = alertStats.value.unacknowledged
    return acknowledged
  }

  async function syncHistory(request: HistorySyncRequest): Promise<HistorySyncJob> {
    const job = await apiRequest<HistorySyncJob>(
      '/api/v1/telegram/channels/sync-history',
      jsonRequest(request),
    )
    upsertHistorySyncJob(job)
    pushNotice('info', tGlobal('notices.historyQueued', { id: job.id.slice(0, 8) }))
    return job
  }

  function applyHistorySyncJob(job: HistorySyncJob): void {
    upsertHistorySyncJob(job)
    if (job.status === 'completed') {
      const synced = job.result?.total_synced ?? 0
      pushNotice('info', tGlobal('notices.historyCompleted', { count: synced.toLocaleString() }))
      void refreshHistoryData().catch((error) => {
        pushNotice('warning', tGlobal('notices.historyRefreshFailed', { error: errorMessage(error) }))
      })
    } else if (job.status === 'failed') {
      pushNotice('error', tGlobal('notices.historyFailed', { error: job.error ?? tGlobal('common.unknownError') }))
      void refreshHistoryData().catch(() => undefined)
    }
  }

  function upsertHistorySyncJob(job: HistorySyncJob): void {
    historySyncJobs.value = [job, ...historySyncJobs.value.filter((item) => item.id !== job.id)]
      .sort((left, right) => Date.parse(right.submitted_at) - Date.parse(left.submitted_at))
      .slice(0, 20)
  }

  async function refreshHistoryData(): Promise<void> {
    const messageParams = new URLSearchParams({ limit: String(INITIAL_MESSAGE_LIMIT) })
    if (messageSenderFilter.value !== null) {
      messageParams.set('sender_id', String(messageSenderFilter.value))
    }
    const [syncStates, refreshedMessages, refreshedChannels] = await Promise.all([
      apiRequest<ChannelSyncState[]>('/api/v1/telegram/channels/sync-state'),
      apiRequest<TelegramMessage[]>(`/api/v1/messages?${messageParams.toString()}`),
      apiRequest<Channel[]>('/api/v1/channels'),
    ])
    channelSyncStates.value = syncStates
    messages.value = refreshedMessages.map(normalizeMessage).sort(newestFirst)
    messagesHasMore.value = refreshedMessages.length >= INITIAL_MESSAGE_LIMIT
    channels.value = refreshedChannels
  }

  async function refreshMonitoringScope(): Promise<void> {
    const messageParams = new URLSearchParams({ limit: String(INITIAL_MESSAGE_LIMIT) })
    if (messageSenderFilter.value !== null) {
      messageParams.set('sender_id', String(messageSenderFilter.value))
    }
    const [refreshedMessages, refreshedAnalyses, refreshedChannels, refreshedStatus, refreshedSenderStats] = await Promise.all([
      apiRequest<TelegramMessage[]>(`/api/v1/messages?${messageParams.toString()}`),
      apiRequest<AnalysisResult[]>('/api/v1/analyses?limit=30'),
      apiRequest<Channel[]>('/api/v1/channels'),
      apiRequest<MonitorStatus>('/api/v1/status'),
      apiRequest<SenderStat[]>('/api/v1/messages/senders?limit=200'),
    ])
    messages.value = refreshedMessages.map(normalizeMessage).sort(newestFirst)
    messagesHasMore.value = refreshedMessages.length >= INITIAL_MESSAGE_LIMIT
    analyses.value = refreshedAnalyses.sort(analysisNewestFirst)
    channels.value = refreshedChannels
    status.value = refreshedStatus
    senderStats.value = refreshedSenderStats
    void loadAlphaOverview().catch(() => undefined)
    void loadIntelligenceGraph().catch(() => undefined)
  }

  function applyMarketAlert(alert: MarketAlert): void {
    marketAlerts.value = [alert, ...marketAlerts.value.filter((item) => item.id !== alert.id)]
      .sort(alertNewestFirst)
      .slice(0, 500)
    alertStats.value.total += 1
    alertStats.value.unacknowledged += alert.acknowledged ? 0 : 1
    alertStats.value[alert.severity] += 1
    alertStats.value[alert.asset_class === 'equity' ? 'equities' : 'crypto'] += 1
    marketStatus.value.alerts_open = alertStats.value.unacknowledged
    if (alert.severity === 'critical' || alert.severity === 'high') {
      ElNotification({
        title: `${alert.severity.toUpperCase()} · ${alert.symbol}`,
        message: alert.title,
        type: alert.severity === 'critical' ? 'error' : 'warning',
        duration: alert.severity === 'critical' ? 0 : 10_000,
        position: 'bottom-right',
      })
    }
  }

  function applyMarketEpisode(episode: MarketAlertEpisode): void {
    marketEpisodes.value = [episode, ...marketEpisodes.value.filter((item) => item.id !== episode.id)]
      .sort(episodeNewestFirst)
      .slice(0, 500)
    marketStatus.value.episodes_open = marketEpisodes.value.filter((item) => item.status === 'open').length
  }

  function pushNotice(level: Notice['level'], message: string): void {
    const id = ++noticeSequence
    notices.value.push({ id, level, message })
    window.setTimeout(() => dismissNotice(id), 6000)
  }

  function dismissNotice(id: number): void {
    notices.value = notices.value.filter((notice) => notice.id !== id)
  }

  function dispose(): void {
    disposed = true
    if (reconnectTimer !== null) window.clearTimeout(reconnectTimer)
    if (senderStatsTimer !== null) window.clearInterval(senderStatsTimer)
    if (onchainRadarRefreshTimer !== null) window.clearTimeout(onchainRadarRefreshTimer)
    reconnectTimer = null
    senderStatsTimer = null
    onchainRadarRefreshTimer = null
    socket?.close(1000, 'component unmounted')
    socket = null
    websocketState.value = 'closed'
  }

  return {
    profile,
    status,
    messages,
    messagesLoadingOlder,
    messagesHasMore,
    messageSenderFilter,
    senderStats,
    analyses,
    alphaOverview,
    alphaLoading,
    intelligenceGraph,
    intelligenceLoading,
    channels,
    telegramDialogs,
    telegramDialogsLoading,
    analysisSettings,
    notificationSettings,
    guestAccounts,
    capabilities,
    memoryStatus,
    marketStatus,
    marketEpisodes,
    marketAlerts,
    marketMovers,
    alertStats,
    onchainRadarItems,
    onchainRadarLoading,
    onchainRadarError,
    onchainStatus,
    onchainStatusLoading,
    onchainStatusError,
    onchainHolderHistory,
    onchainHolderHistoryLoading,
    onchainHolderHistoryError,
    onchainHolderHistoryWindowSeconds,
    onchainSmartFlow,
    onchainSmartFlowLoading,
    onchainSmartFlowError,
    onchainSmartFlowWindowSeconds,
    onchainCryptoGraph,
    onchainCryptoGraphLoading,
    onchainCryptoGraphError,
    onchainCryptoGraphWindowSeconds,
    onchainRadarStateFilter,
    onchainRadarRiskFilter,
    onchainRadarSourceFilter,
    onchainRadarTokenQuery,
    onchainRadarNextBefore,
    onchainRadarNextBeforeId,
    onchainRadarLoadedAt,
    selectedOnchainRadar,
    onchainWalletClusters,
    onchainWalletClustersLoading,
    onchainWalletClustersError,
    onchainEpisodeEvents,
    onchainEpisodeTimelineLoading,
    onchainEpisodeNextBefore,
    onchainEpisodeNextBeforeId,
    channelSyncStates,
    historySyncJobs,
    notices,
    loading,
    bootstrapError,
    websocketState,
    protocolVersion,
    latestAnalysis,
    mediaMessages,
    urgency,
    isRealtime,
    unacknowledgedAlerts,
    historySyncBusy,
    bootstrap,
    refreshMemoryStatus,
    connectRealtime,
    loadOlderMessages,
    loadSenderStats,
    setMessageSenderFilter,
    triggerAnalysis,
    loadAlphaOverview,
    loadIntelligenceGraph,
    loadOnchainRadar,
    loadOnchainStatus,
    loadOnchainHolderHistory,
    setOnchainHolderHistoryWindow,
    loadOnchainSmartFlow,
    setOnchainSmartFlowWindow,
    loadOnchainCryptoGraph,
    setOnchainCryptoGraphWindow,
    setOnchainRadarFilters,
    loadMoreOnchainRadar,
    selectOnchainRadar,
    clearOnchainRadarSelection,
    loadOnchainWalletClusters,
    loadOnchainEpisodeEvents,
    loadMoreOnchainEpisodeEvents,
    requestLoginCode,
    verifyLoginCode,
    verifyPassword,
    loadTelegramDialogs,
    joinAllChannels,
    saveChannelSettings,
    saveAnalysisSettings,
    saveNotificationSettings,
    loadGuestAccounts,
    createGuestAccount,
    updateGuestAccount,
    deleteGuestAccount,
    revokeGuestAccountSessions,
    revokeAllGuestSessions,
    acknowledgeAlert,
    syncHistory,
    pushNotice,
    dismissNotice,
    dispose,
  }
})

function normalizeMessage(message: TelegramMessage): TelegramMessage {
  return {
    ...message,
    sender_kind: message.sender_kind ?? 'unknown',
    media_url: resolveMediaUrl(message),
    sender_avatar_url: resolveSenderAvatarUrl(message),
  }
}

function newestFirst(left: TelegramMessage, right: TelegramMessage): number {
  return Date.parse(right.published_at) - Date.parse(left.published_at)
}

function analysisNewestFirst(left: AnalysisResult, right: AnalysisResult): number {
  return Date.parse(right.created_at) - Date.parse(left.created_at)
}

function alertNewestFirst(left: MarketAlert, right: MarketAlert): number {
  return Date.parse(right.created_at) - Date.parse(left.created_at)
}

function episodeNewestFirst(left: MarketAlertEpisode, right: MarketAlertEpisode): number {
  if (left.status !== right.status) return left.status === 'open' ? -1 : 1
  return Date.parse(right.last_transition_at) - Date.parse(left.last_transition_at)
}

function onchainRadarNewestFirst(left: OnchainRadarView, right: OnchainRadarView): number {
  const byTime = Date.parse(right.evaluated_at) - Date.parse(left.evaluated_at)
  return byTime !== 0 ? byTime : right.episode.id.localeCompare(left.episode.id)
}

function onchainEpisodeEventNewestFirst(
  left: OnchainEpisodeEvent,
  right: OnchainEpisodeEvent,
): number {
  const byTime = Date.parse(right.observed_at) - Date.parse(left.observed_at)
  return byTime !== 0 ? byTime : right.id.localeCompare(left.id)
}

function websocketUrl(): string {
  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${scheme}//${window.location.host}/api/v1/ws`
}

function jsonRequest(payload: object): RequestInit {
  return {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  }
}

async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, init)
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as ApiErrorPayload
    throw new Error(payload.error?.message ?? tGlobal('notices.requestFailed', { status: response.status }))
  }
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

function normalizeNoticeLevel(value: string): Notice['level'] {
  if (value === 'warning' || value === 'error') return value
  return 'info'
}
